/* eslint-disable react/prop-types */
import { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Collapse from "@mui/material/Collapse";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { useTheme, alpha } from "@mui/material/styles";
import { getStorageUrl } from "config/api";

export const SIDEBAR_WIDTH = 264;

/**
 * Modern chmeetings-style sidebar.
 *  - Light background, gradient-tinted active pill with a left accent bar
 *  - Icon chips that fill solid teal when active, soft hover lift
 *  - Collapsible sections (e.g. Departments → list of departments)
 *  - Brand header, quick-action user footer
 */
export default function Sidebar({ routes = [], open, onClose, variant = "permanent" }) {
  const theme = useTheme();
  const { user, church, logout } = useAuth();
  const navigate = useNavigate();
  const palette = theme.palette;

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: SIDEBAR_WIDTH,
          backgroundColor: palette.sidebar.bg,
          backdropFilter: "blur(20px) saturate(180%)",
          WebkitBackdropFilter: "blur(20px) saturate(180%)",
          borderRight: `1px solid ${palette.sidebar.border}`,
          boxShadow: "6px 0 32px rgba(15, 20, 25, 0.06)",
        },
      }}
    >
      <SidebarHeader church={church} />
      <SidebarBody routes={routes} />
      <SidebarFooter user={user} onProfile={() => navigate("/profile")} onLogout={logout} />
    </Drawer>
  );
}

// ---------- Header ----------

function SidebarHeader({ church }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2.5,
        boxShadow: `0 1px 0 ${alpha(theme.palette.sidebar.border, 0.8)}`,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          background: church?.logo
            ? "#fff"
            : `linear-gradient(135deg, ${theme.palette.teal[400]}, ${theme.palette.teal[700]})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          color: "white",
          fontWeight: 800,
          fontSize: 16,
          letterSpacing: 0.5,
          boxShadow: `0 6px 14px ${alpha(theme.palette.teal[600], 0.35)}`,
        }}
      >
        {church?.logo ? (
          <Box
            component="img"
            src={getStorageUrl(church.logo)}
            alt={church.name}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          (church?.abbreviation || church?.name?.slice(0, 2) || "EC")
            .toString()
            .slice(0, 3)
            .toUpperCase()
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ fontWeight: 700, lineHeight: 1.2, color: theme.palette.sidebar.textPrimary }}
        >
          {church?.name || "Ecclesia"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, mt: 0.125 }}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: theme.palette.success.main,
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" noWrap sx={{ color: theme.palette.sidebar.textSecondary }}>
            {church?.city || church?.region || "Member portal"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ---------- Body / nav items ----------

function SidebarBody({ routes }) {
  // Group routes for visual sections; "type:title" rows in the legacy routes
  // become section headers, everything else is rendered as a NavItem.
  const sections = useMemo(() => groupBySection(routes), [routes]);
  return (
    <Box sx={{ flex: 1, overflowY: "auto", py: 1.5 }}>
      {sections.map((section, idx) => (
        <SidebarSection
          key={section.title || `s-${idx}`}
          title={section.title}
          items={section.items}
          first={idx === 0}
        />
      ))}
    </Box>
  );
}

function SidebarSection({ title, items, first }) {
  const theme = useTheme();
  return (
    <Box sx={{ mt: title ? (first ? 1 : 2.5) : 0, mb: 1 }}>
      {title && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 2.5, mb: 0.75 }}>
          <Box
            sx={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              bgcolor: theme.palette.sidebar.iconIdle,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="overline"
            sx={{ color: theme.palette.sidebar.textSecondary, display: "block", lineHeight: 1 }}
          >
            {title}
          </Typography>
        </Box>
      )}
      <List disablePadding>
        {items.map((item) =>
          item.collapse?.length ? (
            <NavGroup key={item.key} item={item} />
          ) : (
            <NavItem key={item.key} item={item} />
          )
        )}
      </List>
    </Box>
  );
}

/** Small icon container that fills solid teal + lifts when its nav item is active. */
function NavIconChip({ children, active, idleColor }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: 1.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background-color .18s ease, color .18s ease, box-shadow .18s ease",
        bgcolor: active ? theme.palette.teal[500] : "transparent",
        color: active ? "#fff" : idleColor,
        boxShadow: active ? `0 4px 10px ${alpha(theme.palette.teal[600], 0.35)}` : "none",
      }}
    >
      {children}
    </Box>
  );
}

function NavItem({ item, nested = false }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const active = isActive(pathname, item.route);
  const palette = theme.palette;

  return (
    <ListItemButton
      component={NavLink}
      to={item.route || "#"}
      sx={{
        position: "relative",
        mx: 1.5,
        mb: 0.375,
        pl: nested ? 4.5 : 1,
        pr: 1.25,
        py: 0.75,
        borderRadius: 2,
        overflow: "hidden",
        color: active ? palette.sidebar.activeFg : palette.sidebar.textPrimary,
        background: active
          ? `linear-gradient(90deg, ${alpha(palette.teal[500], 0.16)}, ${alpha(
              palette.teal[400],
              0.05
            )})`
          : "transparent",
        fontWeight: active ? 700 : 500,
        transition: "background-color .15s ease, transform .15s ease",
        "&:hover": {
          backgroundColor: active ? undefined : palette.sidebar.hover,
          transform: active ? "none" : "translateX(2px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: "18%",
          bottom: "18%",
          width: 3,
          borderRadius: 4,
          bgcolor: active ? palette.teal[500] : "transparent",
        },
      }}
    >
      {!nested && (
        <ListItemIcon sx={{ minWidth: 38 }}>
          <NavIconChip active={active} idleColor={palette.sidebar.iconIdle}>
            {item.icon || <Icon fontSize="small">circle</Icon>}
          </NavIconChip>
        </ListItemIcon>
      )}
      <ListItemText
        primary={item.name}
        primaryTypographyProps={{
          fontSize: 14,
          fontWeight: active ? 700 : 500,
          noWrap: true,
        }}
      />
      {item.badge && (
        <Box
          sx={{
            ml: 1,
            minWidth: 22,
            height: 20,
            px: 0.75,
            borderRadius: 1.25,
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: palette.teal[100],
            color: palette.teal[700],
          }}
        >
          {item.badge}
        </Box>
      )}
    </ListItemButton>
  );
}

function NavGroup({ item }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const palette = theme.palette;
  const containsActive = item.collapse.some((c) => isActive(pathname, c.route));
  const [open, setOpen] = useState(containsActive);

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((o) => !o)}
        sx={{
          mx: 1.5,
          mb: 0.375,
          pl: 1,
          pr: 1.25,
          py: 0.75,
          borderRadius: 2,
          color: containsActive ? palette.sidebar.activeFg : palette.sidebar.textPrimary,
          backgroundColor: containsActive && !open ? palette.sidebar.activeBg : "transparent",
          transition: "background-color .15s ease",
          "&:hover": { backgroundColor: palette.sidebar.hover },
        }}
      >
        <ListItemIcon sx={{ minWidth: 38 }}>
          <NavIconChip active={containsActive} idleColor={palette.sidebar.iconIdle}>
            {item.icon || <Icon fontSize="small">folder</Icon>}
          </NavIconChip>
        </ListItemIcon>
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{ fontSize: 14, fontWeight: containsActive ? 700 : 500 }}
        />
        <Icon
          fontSize="small"
          sx={{
            color: palette.sidebar.iconIdle,
            transition: "transform .18s ease",
            transform: open ? "rotate(180deg)" : "none",
          }}
        >
          expand_more
        </Icon>
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List disablePadding sx={{ mb: 0.5 }}>
          {item.collapse.map((sub) => (
            <NavItem key={sub.key} item={sub} nested />
          ))}
        </List>
      </Collapse>
    </>
  );
}

// ---------- Footer ----------

function SidebarFooter({ user, onProfile, onLogout }) {
  const theme = useTheme();
  return (
    <Box sx={{ p: 1.25, boxShadow: `0 -1px 0 ${alpha(theme.palette.sidebar.border, 0.8)}` }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          px: 1.25,
          py: 1,
          borderRadius: 2.5,
          transition: "background-color .15s ease",
          "&:hover": { bgcolor: theme.palette.sidebar.hover },
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: theme.palette.teal[100],
            color: theme.palette.teal[700],
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {(user?.name?.[0] || "U").toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, lineHeight: 1.15, color: theme.palette.sidebar.textPrimary }}
          >
            {user?.name || "Member"}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: theme.palette.sidebar.textSecondary }}>
            {humanRole(user?.role)}
          </Typography>
        </Box>
        <Tooltip title="My profile">
          <IconButton
            size="small"
            onClick={onProfile}
            sx={{ color: theme.palette.sidebar.textSecondary }}
          >
            <Icon fontSize="small">settings</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Sign out">
          <IconButton
            size="small"
            onClick={onLogout}
            sx={{ color: theme.palette.sidebar.textSecondary }}
          >
            <Icon fontSize="small">logout</Icon>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

// ---------- helpers ----------

function isActive(pathname, route) {
  if (!route || route === "#") return false;
  if (route === "/") return pathname === "/";
  return pathname === route || pathname.startsWith(route + "/");
}

function humanRole(role) {
  if (!role) return "—";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The legacy `routes.js` returns a flat list with mixed "collapse" sections and
 * top-level items. Convert it into [{title, items[]}] so the sidebar can render
 * visual section dividers instead of one long unbroken list.
 *
 * Heuristic:
 *   - "dashboard" stands alone in a "Overview" section
 *   - admin pages: Members, Departments, Ministries, Attendance, Biometric → "Manage"
 *   - accounts/sms/reports → "Finance & comms"
 *   - everything else → "Other"
 */
function groupBySection(routes) {
  const overview = [];
  const manage = [];
  const finance = [];
  const reports = [];
  const other = [];

  for (const r of routes) {
    const key = (r.key || "").toLowerCase();
    if (key === "dashboard" || key === "member" || key === "my-group") overview.push(r);
    else if (
      key === "members" ||
      key.startsWith("all-") ||
      key === "departments" ||
      key === "ministries" ||
      key === "attendance" ||
      key === "biometric" ||
      key === "users"
    )
      manage.push(r);
    else if (key === "accounts" || key === "sms" || key === "tithe" || key === "welfare")
      finance.push(r);
    else if (key === "reports") reports.push(r);
    else other.push(r);
  }

  return [
    { title: "Overview", items: overview },
    { title: "Manage", items: manage },
    { title: "Finance & SMS", items: finance },
    { title: "Reports", items: reports },
    { title: other.length ? "Other" : null, items: other },
  ].filter((s) => s.items.length);
}
